<template>
  <module-template id="scrolltainer" :title="formTitle">
    <v-card>
      <v-card-text>
        <v-form @submit.prevent="submit" class="mx-auto" v-model="valid">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="name"
                :label="$t('global.concepts.name') + '*'"
                :rules="[notEmptyRule, noTagsRule]"
                outlined
                persistent-placeholder
              >
              </v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <retention-category-input
            :label="$t('global.retention-policy.standalone-runs')"
            v-model="standaloneRuns"
          />

          <v-divider class="my-4"></v-divider>

          <retention-category-input
            :label="$t('global.retention-policy.bp-instance-runs')"
            :subtitle="$t('global.retention-policy.bp-instance-runs-subtitle')"
            v-model="bpInstanceRuns"
          />

          <v-divider class="my-4"></v-divider>

          <retention-category-input
            :label="$t('global.retention-policy.bp-instances')"
            v-model="bpInstances"
          />

          <v-divider class="my-4"></v-divider>

          <retention-category-input
            :label="$t('global.retention-policy.unused-files')"
            :subtitle="$t('global.retention-policy.unused-files-subtitle')"
            v-model="unusedFiles"
          />

          <v-divider class="my-4"></v-divider>

          <form-buttons
            :confirmText="$t('global.action.save')"
            :canSubmit="valid"
            @cancel="onCancel"
          ></form-buttons>
        </v-form>
      </v-card-text>
    </v-card>
  </module-template>
</template>

<script>
import { post, get, put } from "@/model/api";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import FormButtons from "@/components/FormButtons";
import RetentionCategoryInput from "@/components/RetentionCategoryInput";
import { notEmpty, noTags } from "@/model/rules";

export default {
  name: "CreateEditRetentionPolicy",

  components: {
    ModuleTemplate,
    FormButtons,
    RetentionCategoryInput,
  },

  props: {
    isEdit: Boolean,
    policyId: Number,
    cancelRoute: String,
  },

  computed: {
    formTitle() {
      if (this.isEdit) {
        return this.$t("global.header.edit", [
          this.$t("global.retention-policy.label").toLowerCase(),
        ]);
      }
      return this.$t("global.header.create", [
        this.$t("global.retention-policy.label").toLowerCase(),
      ]);
    },
  },

  data() {
    return {
      name: "",
      standaloneRuns: { value: null, unit: "DAYS", noDeletion: true },
      bpInstanceRuns: { value: null, unit: "DAYS", noDeletion: true },
      bpInstances: { value: null, unit: "DAYS", noDeletion: true },
      unusedFiles: { value: null, unit: "DAYS", noDeletion: true },
      fileEnvCount: 0,

      // Store original retention settings to detect changes
      originalSettings: null,

      notEmptyRule: notEmpty(this),
      noTagsRule: noTags(this),
      valid: false,
    };
  },

  methods: {
    onCancel() {
      this.$router.push({ name: this.cancelRoute });
    },

    async submit() {
      const data = {
        id: this.policyId,
        name: this.name,
        standaloneRuns: this.standaloneRuns,
        bpInstanceRuns: this.bpInstanceRuns,
        bpInstances: this.bpInstances,
        unusedFiles: this.unusedFiles,
      };

      // Check if we need to show preview confirmation for policy edit
      // Only show preview if retention settings have changed, not just the name
      if (
        this.isEdit &&
        this.fileEnvCount > 0 &&
        this.hasRetentionSettingsChanged()
      ) {
        this.redirectToPreviewConfirmation(data);
        return;
      }

      if (this.isEdit) await this.editPolicy(data);
      else await this.createPolicy(data);
    },

    redirectToPreviewConfirmation(data) {
      // Store pending data in sessionStorage
      sessionStorage.setItem(
        "retention-policy-pending-save",
        JSON.stringify(data)
      );
      // Redirect to preview page with confirmation mode
      this.$router.push({
        name: "Retention policy preview",
        params: { policyId: this.policyId },
        query: { mode: "confirm-policy" },
      });
    },

    async createPolicy(data) {
      return await post("/retention-policy", data)
        .then(() => {
          this.$router.push({ name: "Retention policies" });
        })
        .catch((e) => {
          this.$store.commit("showError", this.createErrorMsg(e));
        });
    },

    async editPolicy(data) {
      return await put("/retention-policy/" + this.policyId, data)
        .then(() => {
          this.$router.push({ name: "Retention policies" });
        })
        .catch((e) => {
          this.$store.commit("showError", this.createErrorMsg(e));
        });
    },

    createErrorMsg(e) {
      if (e.message !== "unique.constraint" || !e.arguments) {
        return e;
      }
      if (e.arguments[0] === "name") {
        return this.$t("global.msg.already-exists", [
          this.$t("global.retention-policy.label").toLowerCase(),
          this.$t("global.concepts.name").toLowerCase(),
          e.arguments[1],
        ]);
      }
      return this.$t("global." + e.message, e.arguments);
    },

    async getPolicy(id) {
      return await get("/retention-policy/" + id).catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    hasRetentionSettingsChanged() {
      if (!this.originalSettings) return true;
      return (
        !this.categoryEquals(
          this.standaloneRuns,
          this.originalSettings.standaloneRuns
        ) ||
        !this.categoryEquals(
          this.bpInstanceRuns,
          this.originalSettings.bpInstanceRuns
        ) ||
        !this.categoryEquals(
          this.bpInstances,
          this.originalSettings.bpInstances
        ) ||
        !this.categoryEquals(
          this.unusedFiles,
          this.originalSettings.unusedFiles
        )
      );
    },

    categoryEquals(a, b) {
      return (
        a.value === b.value &&
        a.unit === b.unit &&
        a.noDeletion === b.noDeletion
      );
    },
  },

  async created() {
    if (this.isEdit) {
      const policy = await this.getPolicy(this.policyId);
      this.name = policy.name;
      this.standaloneRuns = policy.standaloneRuns || {
        value: null,
        unit: "DAYS",
        noDeletion: true,
      };
      this.bpInstanceRuns = policy.bpInstanceRuns || {
        value: null,
        unit: "DAYS",
        noDeletion: true,
      };
      this.bpInstances = policy.bpInstances || {
        value: null,
        unit: "DAYS",
        noDeletion: true,
      };
      this.unusedFiles = policy.unusedFiles || {
        value: null,
        unit: "DAYS",
        noDeletion: true,
      };
      this.fileEnvCount = policy.fileEnvCount || 0;

      // Store original settings for change detection
      this.originalSettings = {
        standaloneRuns: { ...this.standaloneRuns },
        bpInstanceRuns: { ...this.bpInstanceRuns },
        bpInstances: { ...this.bpInstances },
        unusedFiles: { ...this.unusedFiles },
      };
    }
  },
};
</script>
